import { useState, useEffect } from 'react';
import { zzIsOpen } from './opening';

/* Shared helpers used across the marketing site and the booking app.
   __ZZ_DATA/__ZZ_ACTIONS/ZZ_UPCOMING/__ZZ_INBODY are read live off `window`
   deliberately - ZuzuRedesign.tsx publishes the real signed-in member's
   data/actions there at mount. Until that happens (or for a member with no
   real data yet) these resolve to an honest empty/null value — never a
   fabricated default. */

export function __ZZ_DATA(){ return (typeof window!=='undefined' && window.__ZZ_DATA) || {}; }
export function __ZZ_NAME(){ var n=__ZZ_DATA().name; return n || ''; }
export function __ZZ_INITIALS(){ var i=__ZZ_DATA().initials; return i || 'zu'; }
export function __ZZ_FIRST(){ var n=__ZZ_DATA().name; return n ? String(n).trim().split(/\s+/)[0] : 'there'; }
export function __ZZ_NEXTB(){ var u=(typeof window!=='undefined' && window.ZZ_UPCOMING) || []; return u[0] || null; }
export function __ZZ_NEXT_WHAT(){ var n=__ZZ_NEXTB(); return n ? n.what : 'cold plunge + sauna'; }
export function __ZZ_NEXT_TIME(){ var n=__ZZ_NEXTB(); return n ? n.time : '07:30 – 09:00'; }
export function __ZZ_NEXT_START(){ var n=__ZZ_NEXTB(); return (n && n.startISO) || null; }
export function __ZZ_NEXT_KIND(){ var n=__ZZ_NEXTB(); return (n && n.kind) || null; }
export function __ZZ_HAS_NEXT(){ return !!__ZZ_NEXTB(); }
// Friendly relative label for the next booking ("in 25 minutes" / "in 3h" /
// "tomorrow" / "sat 10 may"), computed live from the real start time.
export function __ZZ_NEXT_WHEN_LABEL(){
  var iso = __ZZ_NEXT_START(); if (!iso) return '';
  var start = new Date(iso.replace(' ', 'T')); if (isNaN(start.getTime())) return '';
  var now = new Date();
  var diff = Math.round((start - now) / 60000);
  if (diff <= 0) return 'in progress';
  if (diff < 60) return 'in ' + diff + ' minute' + (diff === 1 ? '' : 's');
  if (start.toDateString() === now.toDateString()) {
    var h = Math.floor(diff / 60), m = diff % 60;
    return 'in ' + h + 'h' + (m ? ' ' + m + 'm' : '');
  }
  var tom = new Date(now); tom.setDate(now.getDate() + 1);
  if (start.toDateString() === tom.toDateString()) return 'tomorrow';
  return start.toLocaleDateString('en-ZA', { weekday: 'short', day: 'numeric', month: 'short' }).toLowerCase();
}
/* Which reads came back as an error rather than as data (see
   useZuzuSession's ZuzuLoadErrors). Every list here defaults to empty, so
   without this a failed read looks exactly like a member who has nothing —
   and the app would tell someone with bookings that they have none. */
export function __ZZ_LOAD_ERRORS(){ return __ZZ_DATA().loadErrors || {}; }
export function __ZZ_LOAD_FAILED(area){ return !!__ZZ_LOAD_ERRORS()[area]; }
/* Re-read the account data after a failed load. No-op on the marketing site
   and in the design preview, where there is no runtime to ask. */
export function __ZZ_RELOAD(){
  var a = (typeof window!=='undefined' && window.__ZZ_ACTIONS) || null;
  if (a && typeof a.reload === 'function') a.reload();
}

export function __ZZ_IB(){ return (typeof window!=='undefined' && window.__ZZ_INBODY) || null; }
export function __ZZ_HAS_IB(){ return !!__ZZ_IB(); }
// Callers must check __ZZ_HAS_IB() first (room.jsx's InbodyResults does) —
// these intentionally return null rather than fabricated scan data when
// there's no real InBody result yet.
export function __IB_LATEST(){ var r=__ZZ_IB(); return r ? r.latest : null; }
export function __IB_TRENDS(){ var r=__ZZ_IB(); return r ? r.trends : null; }
export function __IB_SEG(){ var r=__ZZ_IB(); return r ? r.segments : null; }
export function __IB_TX(){ var r=__ZZ_IB(); return r ? r.trendX : null; }
export function __MACHINE_OF(id){ return ({plunge:'plunge',sauna:'sauna',comp:'compression',red:'redlight',inbody:'inbody',shower:'shower'})[id] || null; }
// Validate/normalise a pasted Spotify playlist link to a canonical
// "spotify:playlist:<id>" URI, or null when it isn't one. Mirrors the SQL
// _zz_normalize_spotify_playlist + the legacy BookingDialog helper.
export function ZZ_NORMALIZE_SPOTIFY(url){
  if (!url || !url.trim()) return null;
  const m = url.match(/playlist[:/]([A-Za-z0-9]{22})/);
  return m ? 'spotify:playlist:' + m[1] : null;
}

/* The club's opening hours (minutes from midnight, SAST), indexed by
   day-of-week the way Date#getDay is — Sunday first.

   THE ONE COPY. These times were previously written out four separate times
   in this file, and the ones in ZZ_SLOTS_FOR decided what a member could
   book while the ones in zzOpenBounds decided whether the site said "open
   now" — so a change to the club's hours could be applied to some of those
   and not others, and nothing would look wrong until a member booked a slot
   at a locked door. Mirrors _zz_open_bounds in SQL, which is what actually
   refuses an out-of-hours booking.

   Weekdays are 06:00–20:00, weekends 08:30–17:00. */
export const ZZ_OPEN_HOURS = [
  { open: 8 * 60 + 30, close: 17 * 60 }, // sunday     08:30–17:00
  { open: 6 * 60, close: 20 * 60 },      // monday     06:00–20:00
  { open: 6 * 60, close: 20 * 60 },      // tuesday
  { open: 6 * 60, close: 20 * 60 },      // wednesday
  { open: 6 * 60, close: 20 * 60 },      // thursday
  { open: 6 * 60, close: 20 * 60 },      // friday
  { open: 8 * 60 + 30, close: 17 * 60 }, // saturday   08:30–17:00
];

// Bookable 10-min start times for a given day offset, from the club's real
// opening hours. Today's already-passed slots are dropped.
const HHMM = (m) =>
  String(Math.floor(m / 60)).padStart(2, '0') + ':' + String(m % 60).padStart(2, '0');

export function ZZ_SLOTS_FOR(dayOffset){
  const d = new Date(); d.setDate(d.getDate() + dayOffset);
  const { open: openM, close: closeM } = ZZ_OPEN_HOURS[d.getDay()];
  const now = new Date();
  const nowM = dayOffset === 0 ? now.getHours() * 60 + now.getMinutes() : -1;
  const slots = [];
  // A member standing in the club can start NOW. The ten-minute grid below is
  // a tidy list to choose from, not a rule about when a visit may begin — on
  // its own it pushed the earliest possible start up to ten minutes away
  // (at 10:03 the first offer was 10:10; at exactly 10:10, 10:20). So when
  // the club is open right now, the current minute leads the list.
  if (nowM >= openM && nowM < closeM) slots.push({ t: HHMM(nowM), now: true });
  for (let m = openM; m < closeM; m += 10) {
    if (m <= nowM) continue;
    slots.push({ t: HHMM(m) });
  }
  return slots;
}

// Default minutes for a room when first added, and the per-room duration limits
// (cold plunge moves in single minutes, the rest in fives; inbody is a fixed
// 5-minute read; the rinse shower defaults to 3 min and stretches to 10).
// Shared by the signed-in stack builder (app.jsx) and the public booking page
// (PageBook.jsx) so the two can't offer different durations.
export const ZZ_DEFAULT_DUR = (id) => id === 'shower' ? 3 : id === 'plunge' ? 3 : id === 'inbody' ? 5 : id === 'red' ? 10 : 20;
export const ZZ_DUR_LIMITS = {
  plunge: { min: 1, max: 10, step: 1 },
  sauna: { min: 10, max: 60, step: 5 },
  comp: { min: 10, max: 40, step: 5 },
  red: { min: 5, max: 30, step: 5 },
  inbody: { min: 5, max: 5, step: 5 },
  shower: { min: 3, max: 10, step: 1 }
};

// The selectable durations for a room, as a short list — a picker's worth of
// choices rather than every step between min and max (which would be ten
// buttons for the plunge). Always includes the default and the max.
export function ZZ_DUR_OPTIONS(id){
  const c = ZZ_DUR_LIMITS[id] || { min: 5, max: 60, step: 5 };
  if (c.min === c.max) return [c.min];
  const span = c.max - c.min;
  const step = Math.max(c.step, Math.round(span / 4 / c.step) * c.step || c.step);
  const out = [];
  for (let m = c.min; m <= c.max; m += step) out.push(m);
  if (out[out.length - 1] !== c.max) out.push(c.max);
  const def = ZZ_DEFAULT_DUR(id);
  if (def >= c.min && def <= c.max && !out.includes(def)) out.push(def);
  return out.sort((a, b) => a - b);
}

// Private-room price (Rands) for a window + duration in minutes. The 30-minute
// slot is a flat tier, deliberately NOT half the hourly rate; longer bookings
// round up to whole hours. Mirrors the SQL _zz_room_credits and the edge
// function so the displayed price always matches what's charged.
//   off-peak: 30 min = R600,  per hour = R1,000
//   peak:     30 min = R1,200, per hour = R2,000
export function ZZ_ROOM_PRICE(win, minutes){
  const peak = win === 'peak';
  if (minutes <= 0) return 0;
  if (minutes <= 30) return peak ? 1200 : 600;
  return Math.ceil(minutes / 60) * (peak ? 2000 : 1000);
}
export function ZZ_ROOM_CREDITS(win, minutes){ return ZZ_ROOM_PRICE(win, minutes) / 100; }

// Closing time (minutes from midnight, SAST) for a day offset. This is what
// greys a start time out when the chosen duration would run past closing —
// the club shutting at 17:00 on a Sunday has to stop a 16:50 sauna, not just
// a 17:10 one.
export function ZZ_CLOSE_FOR(dayOffset){
  const d = new Date(); d.setDate(d.getDate() + dayOffset);
  return ZZ_OPEN_HOURS[d.getDay()].close;
}

// Off-peak window bounds (minutes from midnight, SAST) for the day a Date falls
// on. Mirrors the SQL _zz_off_peak_bounds — the single source of truth for the
// per-day peak/off-peak schedule:
//   Sun: fully off-peak (06:00–20:00) · Sat: off-peak from 13:00 · Mon–Fri: 09:00–16:00.
export function ZZ_OFF_PEAK_BOUNDS(date){
  const dow = date.getDay();
  if (dow === 0) return { start: 6 * 60, end: 20 * 60 };
  if (dow === 6) return { start: 13 * 60, end: 20 * 60 };
  return { start: 9 * 60, end: 16 * 60 };
}

// Opening hours (minutes from midnight, SAST) per day-of-week.
function zzOpenBounds(dow) {
  return ZZ_OPEN_HOURS[dow];
}

// Are the doors open right this minute? Both halves matter: the club hasn't
// opened at all before ZZ_OPENING_DATE, and on any day it's shut outside the
// hours above. A live "N open now" count is a true number attached to a false
// claim when either is the case — at 2am every room is "free", and nobody can
// use one. Callers show capacity instead of availability when this is false.
export function ZZ_IS_OPEN_NOW(now = new Date()) {
  if (!zzIsOpen(now)) return false;
  // Read the wall clock in SAST rather than the visitor's own zone: someone
  // checking from London should be told what Century City's doors are doing.
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Africa/Johannesburg',
    weekday: 'short', hour: '2-digit', minute: '2-digit', hour12: false,
  }).formatToParts(now);
  const get = (t) => (parts.find((p) => p.type === t) || {}).value;
  const dow = { sun: 0, mon: 1, tue: 2, wed: 3, thu: 4, fri: 5, sat: 6 }[
    String(get('weekday') || '').slice(0, 3).toLowerCase()
  ];
  if (dow === undefined) return false;
  const mins = Number(get('hour')) * 60 + Number(get('minute'));
  const { open, close } = zzOpenBounds(dow);
  return mins >= open && mins < close;
}

const zzHM = (m) => {
  const h24 = Math.floor(m / 60);
  const mins = m % 60;
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
  const suffix = h24 < 12 ? 'am' : 'pm';
  return mins ? `${h12}:${String(mins).padStart(2, '0')}${suffix}` : `${h12}${suffix}`;
};

/**
 * The peak / off-peak schedule, for DISPLAY, derived from the same
 * ZZ_OFF_PEAK_BOUNDS + opening hours that billing uses. Deriving it rather than
 * writing the times out by hand is the point: the schedule was previously
 * restated as prose in a dozen places, so a rate change could leave the site
 * advertising hours it no longer charges.
 *
 * Returns one row per day-group: { label, offPeak: string, peak: string }.
 */
export function ZZ_PEAK_SCHEDULE() {
  // One representative day per group: weekdays behave identically, and Sat/Sun
  // each differ. Dates chosen only for their day-of-week.
  const groups = [
    { label: 'mon – fri', dow: 1 },
    { label: 'saturday', dow: 6 },
    { label: 'sunday', dow: 0 },
  ];
  return groups.map(({ label, dow }) => {
    const { open, close } = zzOpenBounds(dow);
    const bounds = ZZ_OFF_PEAK_BOUNDS({ getDay: () => dow });
    // Clipped to the doors. The rate windows are set independently of opening
    // hours, so Saturday's off-peak runs to 20:00 on paper while the club
    // shuts at 17:00 — quoting the paper version would advertise three hours
    // nobody can book. Only the DISPLAY is clipped: pricing keeps the real
    // bounds, which is harmless because no booking can exist out there.
    const offStart = Math.max(bounds.start, open);
    const offEnd = Math.min(bounds.end, close);
    const peakParts = [];
    if (offStart > open) peakParts.push(`${zzHM(open)}–${zzHM(offStart)}`);
    if (offEnd < close) peakParts.push(`${zzHM(offEnd)}–${zzHM(close)}`);
    return {
      label,
      offPeak: `${zzHM(offStart)}–${zzHM(offEnd)}`,
      // Sunday has no peak window at all, and saying so is clearer than a blank.
      peak: peakParts.length ? peakParts.join(' · ') : 'no peak rate',
      allOffPeak: peakParts.length === 0,
    };
  });
}

// Whether [startMin, startMin+durationMin) is entirely off-peak, entirely peak,
// or straddles the boundary, for the given Date.
export function ZZ_WINDOW_FOR(date, startMin, durationMin){
  const { start: offStart, end: offEnd } = ZZ_OFF_PEAK_BOUNDS(date);
  const endMin = startMin + durationMin;
  const offMin = Math.max(0, Math.min(endMin, offEnd) - Math.max(startMin, offStart));
  const peakMin = Math.max(0, durationMin - offMin);
  if (offMin > 0 && peakMin > 0) return 'pro-rata';
  return peakMin > 0 ? 'peak' : 'off-peak';
}

// Credits for a single-room / session booking window: 30 min per credit
// off-peak, 15 min per credit peak, pro-rated when the window straddles the
// boundary, minimum one credit. Mirrors the SQL _zz_credit_units + the
// GREATEST(1, CEIL(...)) its callers apply, so a price shown before booking
// matches what's charged.
export function ZZ_SESSION_CREDITS_FOR(date, startMin, durationMin){
  if (durationMin <= 0) return 0;
  const { start: offStart, end: offEnd } = ZZ_OFF_PEAK_BOUNDS(date);
  const endMin = startMin + durationMin;
  const offMin = Math.max(0, Math.min(endMin, offEnd) - Math.max(startMin, offStart));
  const peakMin = durationMin - offMin;
  return Math.max(1, Math.ceil(offMin / 30 + peakMin / 15));
}

// Credits an UNLIMITED (off-peak) member is actually charged for a single/
// session window: only the peak minutes count, at 15 min/credit, rounded up —
// off-peak minutes are covered by the membership and cost nothing. Mirrors
// the SQL _zz_peak_credit_units + the CEIL(...) confirm_machine_booking and
// confirm_session_booking apply, so the price shown before booking matches
// what a subscriber is actually charged.
export function ZZ_PEAK_CREDITS_FOR(date, startMin, durationMin){
  if (durationMin <= 0) return 0;
  const { start: offStart, end: offEnd } = ZZ_OFF_PEAK_BOUNDS(date);
  const endMin = startMin + durationMin;
  const offMin = Math.max(0, Math.min(endMin, offEnd) - Math.max(startMin, offStart));
  const peakMin = Math.max(0, durationMin - offMin);
  return peakMin > 0 ? Math.ceil(peakMin / 15) : 0;
}

// What a multi-station session costs, priced PER STATION rather than across the
// whole span. That distinction only surfaces once a session can be EDITED:
// re-time one station and a gap can open between two others, and a member must
// not pay for minutes they are not booked into anything. Mirrors the per-step
// sum in confirm_session_booking / edit_session_booking, which add
// _zz_credit_units over each window rather than over first-start to last-end —
// so the fractional units are summed and the ceiling applied ONCE, not per
// station.
//
// steps: [{ startMin, minutes }] in any order (they are priced independently).
export function ZZ_STACK_UNITS_FOR(date, steps){
  const { start: offStart, end: offEnd } = ZZ_OFF_PEAK_BOUNDS(date);
  return (steps || []).reduce((sum, s) => {
    const minutes = s && s.minutes > 0 ? s.minutes : 0;
    if (!minutes) return sum;
    const startMin = s.startMin || 0;
    const endMin = startMin + minutes;
    const offMin = Math.max(0, Math.min(endMin, offEnd) - Math.max(startMin, offStart));
    return sum + offMin / 30 + (minutes - offMin) / 15;
  }, 0);
}

export function ZZ_STACK_CREDITS_FOR(date, steps){
  const units = ZZ_STACK_UNITS_FOR(date, steps);
  return units <= 0 ? 0 : Math.max(1, Math.ceil(units));
}

// The extra credits an edit costs: the re-priced session minus what the member
// has already paid for it. Never negative — a shorter edit pays back through
// ZZ_EDIT_REFUND below instead. So an edit that does not cross a credit
// boundary is free, which is the common case: off-peak bills per 30 minutes,
// so stretching 48 minutes to 58 still costs 2.
export function ZZ_EDIT_TOP_UP(date, steps, paidCredits){
  const paid = Math.ceil(Math.max(0, paidCredits || 0));
  return Math.max(0, ZZ_STACK_CREDITS_FOR(date, steps) - paid);
}

// What a SHORTER edit pays back. Releasing booked minutes is a
// part-cancellation, so it refunds on the cancellation tier — applied to the
// credits released, not to the whole booking. Mirrors edit_session_booking in
// 20260907060000.
//
// `refundPct` is a PERCENTAGE (100 / 50 / 0), passed in rather than derived
// here so the tiers keep living in one place: callers get it from
// cancelTermsFor()/refundPctFor() in bookingLogic.ts, measured on notice to the
// session's FIRST start. Inside 12 hours it is 0, which is what stops a
// mid-session shorten from becoming an early-leave refund.
//
// FLOOR, like every other refund path: the club never pays back a credit it
// did not take.
export function ZZ_EDIT_REFUND(date, steps, paidCredits, refundPct){
  const paid = Math.max(0, paidCredits || 0);
  const released = Math.max(0, paid - ZZ_STACK_CREDITS_FOR(date, steps));
  const pct = Math.max(0, Math.min(100, refundPct || 0));
  return Math.floor((released * pct) / 100);
}

// Private-room credits for a specific booking window, pro-rated when it
// straddles the peak boundary. Pure off-peak/peak windows keep the existing
// 30-min flat tier + hourly rate; a straddling booking is billed on the
// continuous hourly rate (10 cr/hr off-peak, 20 cr/hr peak) per portion instead
// of the flat tier. Mirrors the SQL _zz_room_credits exactly.
export function ZZ_ROOM_CREDITS_FOR(date, startMin, durationMin){
  if (durationMin <= 0) return 0;
  const { start: offStart, end: offEnd } = ZZ_OFF_PEAK_BOUNDS(date);
  const endMin = startMin + durationMin;
  const offMin = Math.max(0, Math.min(endMin, offEnd) - Math.max(startMin, offStart));
  const peakMin = durationMin - offMin;
  if (offMin > 0 && peakMin > 0) return Math.ceil(offMin * (10 / 60) + peakMin * (20 / 60));
  const peak = peakMin > 0;
  if (durationMin <= 30) return peak ? 12 : 6;
  return Math.ceil(durationMin / 60) * (peak ? 20 : 10);
}

export function ZZ_ROOM_PRICE_FOR(date, startMin, durationMin){
  return ZZ_ROOM_CREDITS_FOR(date, startMin, durationMin) * 100;
}

export function useAvail(day, mode, singleRoom, stack, unitMachine, activeDur){
  const [map, setMap] = useState(null);
  // The first slot for today is the current minute (see ZZ_SLOTS_FOR), so the
  // list has to be re-derived as the clock moves — otherwise "now" freezes at
  // whatever minute the picker opened on, and a member who sat deciding for
  // five minutes books a window that already started.
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 30000);
    return () => clearInterval(id);
  }, []);
  // unitMachine (e.g. 'plunge-1') overrides the modality's default machine.
  const machineId = mode === 'room' ? 'room' : (unitMachine || __MACHINE_OF(singleRoom));
  const dur = Number(activeDur) || 0;
  const base = ZZ_SLOTS_FOR(day);
  // For a session, availability is the min free across every station's window,
  // so the key tracks the whole stack (machine + minutes), not one machine.
  const sessionSteps = (mode === 'session' && stack)
    ? stack.map((x) => ({ machine_id: __MACHINE_OF(x.id), minutes: x.duration })).filter((s) => s.machine_id)
    : [];
  // The chosen duration is part of the key: single/room availability is checked
  // across the whole [start, start+duration) window, so it must refetch when the
  // member changes how long they're booking.
  // The "now" slot's own minute is part of the key: without it the fetch keeps
  // asking about the minute the picker opened on, and the current one shows as
  // free whether it is or not.
  const nowSlot = base[0] && base[0].now ? base[0].t : '';
  const key = (mode === 'session'
    ? 'session|' + day + '|' + sessionSteps.map((s) => s.machine_id + ':' + s.minutes).join(',')
    : (machineId || '') + '|' + day + '|' + dur) + '|' + nowSlot;
  useEffect(() => {
    let alive = true;
    const acts = (typeof window !== 'undefined') && window.__ZZ_ACTIONS;
    const d = new Date(); d.setDate(d.getDate() + day);
    const iso = d.getFullYear() + '-' + String(d.getMonth()+1).padStart(2,'0') + '-' + String(d.getDate()).padStart(2,'0');
    const fetchNow = () => {
      if (mode === 'session') {
        if (!acts || !acts.sessionAvailability || !sessionSteps.length) { setMap(null); return; }
        acts.sessionAvailability(iso, sessionSteps, base.map((s) => s.t)).then((res) => { if (alive) setMap(res || null); });
        return;
      }
      if (!acts || !acts.availability || !machineId) { setMap(null); return; }
      acts.availability(iso, machineId, base.map((s) => s.t), dur).then((res) => { if (alive) setMap(res || null); });
    };
    fetchNow();
    // Re-check while the member sits on the picker deciding — otherwise a slot
    // that looked open when the page loaded can still look open (and be
    // confirmable) minutes later after someone else has taken it, with the
    // clash only surfacing as a failed "already booked" toast at confirm time.
    const id = setInterval(fetchNow, 20000);
    return () => { alive = false; clearInterval(id); };
  }, [key]);
  // A start time is also unavailable if the booking would run past closing time.
  const closeM = ZZ_CLOSE_FOR ? ZZ_CLOSE_FOR(day) : 24 * 60;
  const toMin = (t) => { const [h, m] = t.split(':').map(Number); return h * 60 + m; };
  const overflows = (t) => dur > 0 && (toMin(t) + dur) > closeM;
  // 'now' is worth saying out loud in the picker — it's the difference between
  // "the next slot is 10:10" and "you can start right now".
  const label = (s, txt) => (s.now ? 'now · ' + txt : txt);
  return base.map((s) => {
    if (overflows(s.t)) return Object.assign({}, s, { full: true, sub: label(s, 'too long') });
    if (!map) return s.now ? Object.assign({}, s, { sub: 'now' }) : s;
    const free = map[s.t];
    if (free === undefined) return s.now ? Object.assign({}, s, { sub: 'now' }) : s;
    return Object.assign({}, s, {
      full: free <= 0,
      sub: label(s, free <= 0 ? 'full' : free + ' left'),
    });
  });
}
